import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["filter", "domainFilter"];

  connect() {
    this.attachedCompetencies();
    this.nocps();
  }

  initialize() {
    this.isAddingCompetency = false;
  }

  setFilter(event) {
    event.preventDefault();
    const value = event.target.dataset.value;
    this.element.dataset.filter = value;
    this.reloadCurrentFilter();

    this.filterTargets.forEach((element) => {
      element.classList.remove("active");
    });
    event.target.classList.add("active");
  }

  domainChanged() {
    this.reloadCurrentFilter();
  }

  reloadCurrentFilter() {
    const filter = this.element.dataset.filter;
    if (filter === "nocps") {
      this.nocps();
    } else if (filter === "non-nocps") {
      this.nonNocps();
    } else {
      this.allCompetencies();
    }
  }

  nocps() {
    fetch(this.fetchUrl("/learning_objectives/nocps.json"))
      .then((response) => response.json())
      .then((data) => {
        this.buildPickerList(data);
      });
  }

  nonNocps() {
    fetch(this.fetchUrl("/learning_objectives/non_nocps.json"))
      .then((response) => response.json())
      .then((data) => {
        this.buildPickerList(data);
      });
  }

  allCompetencies() {
    fetch(this.fetchUrl("/learning_objectives/all_competencies.json"))
      .then((response) => response.json())
      .then((data) => {
        this.buildPickerList(data);
      });
  }

  fetchUrl(path) {
    const domain = this.selectedDomain();
    if (!domain || domain === "all") return path;

    const separator = path.includes("?") ? "&" : "?";
    return `${path}${separator}practice_domain=${encodeURIComponent(domain)}`;
  }

  selectedDomain() {
    if (!this.hasDomainFilterTarget) return "all";
    return this.domainFilterTarget.value;
  }

  removeCompetency(event) {
    if (this.isAddingCompetency) return;
    this.isAddingCompetency = true;
    const competency_id = event.currentTarget.dataset.id;
    const course_id = this.element.dataset.courseId;

    fetch(
      `/courses/${course_id}/remove_objective?learning_objective_id=${competency_id}`
    )
      .then((response) => {
        if (response.ok) {
          this.attachedCompetencies();
          this.reloadCurrentFilter();
        } else {
          alert("Something went wrong");
        }
      })
      .finally(() => {
        this.isAddingCompetency = false;
      });
  }

  attachedCompetencies() {
    const course_id = this.element.dataset.courseId;
    return fetch(`/courses/${course_id}/attached_objectives.json`)
      .then((response) => response.json())
      .then((data) => {
        this.buildAttachedList(data);
        return data;
      });
  }

  addCompetency(event) {
    if (this.isAddingCompetency) return;
    this.isAddingCompetency = true;
    const competency_id = event.currentTarget.dataset.id;
    const course_id = this.element.dataset.courseId;

    fetch(
      `/courses/${course_id}/add_objective?learning_objective_id=${competency_id}`
    )
      .then((response) => {
        if (response.ok) {
          this.attachedCompetencies();
          this.reloadCurrentFilter();
        } else {
          alert("Something went wrong");
        }
      })
      .finally(() => {
        this.isAddingCompetency = false;
      });
  }

  buildAttachedList(data) {
    const scrollPosition = document.getElementById("attached-ul")?.scrollTop;
    const list = document.createElement("ul");
    list.id = "attached-ul";
    list.classList.add("list-group", "scrollable-list");
    let last_category = "";
    data.forEach((element) => {
      if (last_category != element.category) {
        const category = document.createElement("li");
        category.classList.add(
          "list-group-item",
          "disabled",
          "lo-category-list-item"
        );
        category.innerText = element.category;
        list.appendChild(category);
        last_category = element.category;
      }
      const item = document.createElement("li");

      item.classList.add("list-group-item", "lo-list-list-item");
      const remove_button = document.createElement("span");
      remove_button.dataset.id = element.id;
      remove_button.dataset.action =
        "click->competency-picker#removeCompetency";
      remove_button.classList.add("remove-comp-btn");
      const icon = document.createElement("i");
      icon.classList.add("fa", "fa-trash");
      remove_button.appendChild(icon);
      const name_line = document.createElement("div");
      name_line.classList.add("comp-list-item-row");
      name_line.innerText = element.name;
      name_line.appendChild(remove_button);
      item.appendChild(name_line);

      if (element.nocp) {
        const perf_env = document.createElement("div");
        perf_env.classList.add("nocp-perf-env");
        perf_env.innerText = element.description?.split("\n")[0];
        item.appendChild(perf_env);
      }
      list.appendChild(item);
    });

    document.getElementById("attached-list").innerHTML = "";
    document.getElementById("attached-list").appendChild(list);
    document.getElementById("attached-ul").scrollTop = scrollPosition;
  }

  async buildPickerList(data) {
    const scrollPosition = document.getElementById("picker-ul")?.scrollTop;

    const attachedList = await this.attachedCompetencies();
    const list = document.createElement("ul");
    list.id = "picker-ul";
    list.classList.add("list-group", "scrollable-list");
    let last_category = "";
    data.forEach((element) => {
      if (last_category != element.category) {
        const category = document.createElement("li");
        category.classList.add("list-group-item", "lo-category-list-item");
        const name_div = document.createElement("div");
        name_div.classList.add("comp-list-item-row");
        name_div.innerText = element.category;
        category.appendChild(name_div);
        list.appendChild(category);
        last_category = element.category;
      }
      const item = document.createElement("li");
      if (attachedList.some((attached) => attached.id === element.id)) {
        item.dataset.id = element.id;
        item.classList.add(
          "list-group-item",
          "lo-list-list-item",
          "disabled",
          "strikethrough"
        );
        const name_line = document.createElement("div");
        name_line.innerText = element.name;
        item.appendChild(name_line);
        if (element.nocp) {
          const perf_env = document.createElement("div");
          perf_env.classList.add("nocp-perf-env");
          perf_env.innerText = element.description?.split("\n")[0];
          item.appendChild(perf_env);
        }
      } else {
        item.dataset.action = "click->competency-picker#addCompetency";
        item.dataset.id = element.id;
        item.classList.add("list-group-item", "lo-list-list-item", "clickable");
        const name_line = document.createElement("div");
        name_line.innerText = element.name;

        item.appendChild(name_line);
        if (element.nocp) {
          const perf_env = document.createElement("div");
          perf_env.classList.add("nocp-perf-env");
          perf_env.innerText = element.description?.split("\n")[0];
          item.appendChild(perf_env);
        }
      }
      list.appendChild(item);
    });

    document.getElementById("picker-list").innerHTML = "";
    document.getElementById("picker-list").appendChild(list);
    document.getElementById("picker-ul").scrollTop = scrollPosition;
  }
}
